import React, { useState } from 'react';
import Image from 'next/legacy/image';
import cx from 'classnames';

import { IGlcePartnerConfig, IHeader2Fields } from '@/types/contentful';

import { useEscapeEvent } from '../../../../../hooks/useEscapeEvent';
import { getImageDataWithHttps } from '../../../../../utils/contentful';
import { useHasClassroomsInCurrentUrl } from '../../../../../hooks/useHasClassroomsInCurrentUrl';

import { NavigationLink } from './DesktopNavLink';
import CtaWidget from '../../../../CtaWidget';
import Link from '../../../../Link';
import { ProfileDropdownMenu } from '../../../ProfileDropdownMenu';
import { ResourcesDropdown } from '../../../../Classrooms/ResourcesDropdown';

import defaultFullLogo from '@/images/gl-logo-full-large.svg';

interface Props extends Partial<IHeader2Fields> {
  isScrolled: boolean;
  isSimplified: boolean;
  isSubNavigationPresent?: boolean;
  partner?: string;
  glcePartnerConfig?: IGlcePartnerConfig;
}

const defaultFullLogoData = {
  ...defaultFullLogo,
  alt: 'Greenlight logo'
};

export function DesktopNavbar({
  isScrolled,
  isSimplified,
  navItems,
  desktopCta,
  fullLogoDesktop,
  abbreviatedLogoDesktop,
  linkToHome,
  showAuthenticatedUser,
  roundedCornersVariation,
  isSubNavigationPresent,
  partner,
  glcePartnerConfig
}: Props) {
  const [selected, setSelected] = useState<string | undefined>(undefined);
  const hasClassrooms = useHasClassroomsInCurrentUrl();
  const abbreviatedLogoData = abbreviatedLogoDesktop
    ? getImageDataWithHttps(abbreviatedLogoDesktop)
    : undefined;

  const fullLogoData = fullLogoDesktop
    ? getImageDataWithHttps(fullLogoDesktop)
    : defaultFullLogoData;

  useEscapeEvent(() => setSelected(undefined));

  return (
    <div
      className={cx(
        'relative z-[9999] mx-auto hidden h-[72px] w-full items-center justify-center bg-white',
        {
          'rounded-b-[40px]': roundedCornersVariation,
          'shadow-[0px_4px_4px_rgba(0,0,0,0.08)]':
            isScrolled && !isSubNavigationPresent
        },
        'desktop:flex'
      )}
      data-testid="desktop-nav"
    >
      <div className="flex w-[90vw] max-w-[1440px] items-center justify-between">
        <Link
          data-testid="desktop-logo-link"
          href={linkToHome ? linkToHome : '/'}
        >
          {isScrolled && !showAuthenticatedUser && !!abbreviatedLogoData ? (
            <div className={cx(hasClassrooms ? 'h-9' : 'h-6')}>
              <Image
                src={abbreviatedLogoData.src}
                alt={abbreviatedLogoData.alt}
                height={!partner ? abbreviatedLogoData.height : 36}
                width={!partner ? abbreviatedLogoData.width : 240}
                unoptimized={true}
              />
            </div>
          ) : (
            <div className={cx(hasClassrooms ? 'h-9 w-60' : 'h-6 w-40')}>
              <Image
                src={fullLogoData.src}
                alt={fullLogoData.alt}
                height={36}
                width={240}
                unoptimized={true}
                style={{ objectFit: 'contain' }}
              />
            </div>
          )}
        </Link>

        <div
          className={cx(
            'ml-8 mr-auto flex w-full flex-row',
            {
              hidden: isSimplified
            },
            'desktop:max-w-[640px]'
          )}
          data-testid="desktop-navlinks"
        >
          {navItems &&
            navItems.map(({ fields }: any, idx: number) => (
              <NavigationLink
                {...fields}
                key={idx}
                selected={selected === (fields?.name || fields?.title)}
                setSelected={setSelected}
              />
            ))}
        </div>

        {desktopCta && !showAuthenticatedUser && (
          <CtaWidget
            {...desktopCta?.fields}
            id="desktop-nav-cta"
            sysId={desktopCta?.sys?.contentType?.sys?.id}
            module="Navigation"
          />
        )}

        {showAuthenticatedUser && (
          <div className="flex items-center gap-12">
            {partner && (
              <ResourcesDropdown
                partnerLogo={abbreviatedLogoData}
                customLinks={glcePartnerConfig?.fields?.customLinks}
                partnerDisplayName={
                  glcePartnerConfig?.fields?.partnerDisplayName
                }
              />
            )}
            <ProfileDropdownMenu isPartner={!!partner} />
          </div>
        )}
      </div>
    </div>
  );
}
